import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useParams, Link } from 'react-router-dom';
import { Breadcrumb, BackdropCircular } from 'components';
import {
  LinearProgress,
  Grid,
  Card,
  CardActions,
  Button,
  Tabs,
  Tab,
  Box
} from '@material-ui/core';
import { TabContext, TabPanel } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import {
  getStoreProductsApi,
  getStoreBrandsApi,
  getAllTasksFromQueueApi
} from 'api';
import StoreProductsList from 'pages/catalog/storeProductsList';
import StoreProductsQueue from 'pages/catalog/storeProductsQueue';
import logo from 'components/Logo/logo-dermpro.svg';
import listingsImage from 'components/Pages/listings-img.svg';
import { imgBaseUrl } from 'configs';
import useProducts from 'hooks/useProducts';


const useStyles = makeStyles(() => ({
  rootTabs: {
    color: '#828282',
    '& .MuiTab-root': {
      textTransform: 'uppercase',
      fontSize: '12px',
      fontWeight: 600
    },
    '& .MuiTabs-indicator': {
      display: 'none'
    },
    '& .Mui-selected': {
      color: '#4277C6'
    },
    '& .MuiTab-textColorInherit': {
      opacity: 1
    }
  },
  tabsTitles: {
    textTransform: 'capitalize',
  }
}));


const StoreProducts = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const {selectedTab = 'active'} = useParams<{selectedTab?: string}>();

  const [catalog, setCatalog] = useState([]);

  const { storeBrands } = useSelector((state: IReduxState) => state.brands);
  const {profile} = useSelector((state: IReduxState) => state.user);

  const userStore = profile?.stores?.[0];
  const storeViewCode = userStore.storeCode;
  const permissions = profile?.config?.permissions || {};

  const {
    allStoreProducts,
    activeProducts,
    disabledProducts,
    queueTasks,
    queueTasksSkus,
    productsLoading,
    error,
    deletingProduct,
  } = useProducts(userStore.storeCode);


  const handleChangeTab = (event, newValue) => {
    history.push(newValue);
  };

  const mediaStoreUrl = `${userStore?.apiUrl}/media/catalog/product` || imgBaseUrl;

  useEffect(() => {
    if (!queueTasks.length) {
      getAllTasksFromQueueApi({dispatch, storeViewCode});
    }

    if (!storeBrands.length) {
      getStoreBrandsApi({dispatch});
    }

    if (!allStoreProducts.length && userStore?.isCreated) {
      getStoreProductsApi({dispatch});
    }
  }, [userStore?.isCreated]);

  useEffect(() => {
    if (selectedTab === 'queue' && !queueTasks.length) {
      history.push('draft');
    }
  }, [queueTasks.length]);

  useEffect(() => {
    if (storeBrands?.length) {
      setCatalog([...allStoreProducts.map((product) => {
        const productBrand = product?.custom_attributes?.find((attr) => attr.attribute_code === 'brand')?.value;
        const productImagePath = product?.custom_attributes?.find((attr) => attr.attribute_code === 'image')?.value;

        return {
          ...product,
          image: productImagePath ? `${mediaStoreUrl}${productImagePath}` : logo,
          price: +Number.parseFloat(product?.price).toFixed(2) || '0',
          brand: storeBrands?.find((b) => b.option_id === productBrand)?.value || '',
          status: product?.status === 1 ? 'Enabled' : 'Disabled',
          subscription_active: product?.custom_attributes?.find((attr) => attr.attribute_code === 'subscription_active')?.value || '0'
        };
      })]);
    }
  }, [allStoreProducts, storeBrands]);

  return (
    <>
      <div className="flex-grow h-full min-h-full-screen scroll-y pb-25">
        <div className="mb-sm-30">
          <Breadcrumb
            routeSegments={[
              { name: 'Listings' }
            ]}
          />
        </div>
        {!productsLoading && !error && catalog?.length > 0 &&
          <Grid container className="flex px-8 pt-0 pb-4" justifyContent="flex-end">
            <div>
              {permissions?.listingsLibrary &&
              <Link to="/browse-brand/store">
                <Button
                  type="button"
                  color="primary"
                  variant="contained"
                  className="uppercase custom-check-all custom-check-all-select mr-2"
                  title="Click here to add a standard/common SKU"
                >
                  Add product from Library
                </Button>
              </Link>
              }
              {permissions?.listingsAddNew &&
              <Link to="/request-sku">
                <Button
                  type="button"
                  color="primary"
                  variant="outlined"
                  className="uppercase custom-check-all"
                  title="Click here to add a custom/specialty SKU"
                >
                  Request Custom Listing
                </Button>
              </Link>
              }
            </div>
          </Grid>}
        <div className="mx-10">
          {!productsLoading && error && <div>Something went wrong</div>}
          {productsLoading && <LinearProgress className="mt-10" />}
          {!productsLoading && !error && (!catalog?.length && !allStoreProducts.length) &&
            <Grid
              container
              spacing={0}
              direction="column"
              alignItems="center"
              justifyContent="center"
              className="mt-20"
            >
              <div className="flex-grow flex-column relative scroll-y justify-center items-center">
                <img src={listingsImage} alt="under reconstruction" width="258" />
              </div>
              <h2 className="font-bold py-6 text-center text-deep-gray">Add and manage your listings</h2>
              <p className="py-6 text-center">This is where you’ll add listings to your store and configure them.</p>
              <CardActions>
                {permissions?.listingsLibrary &&
                  <Link to="/browse-brand/store">
                    <Button variant="contained"  color="primary" className="uppercase">
                      Add product from Library
                    </Button>
                  </Link>
                }
                {permissions?.listingsAddNew &&
                  <Link to="/request-sku">
                    <Button variant="outlined" color="primary" className="uppercase">
                      Request Custom Listing
                    </Button>
                  </Link>
                }
              </CardActions>
            </Grid>
          }
          {!productsLoading && !error && (catalog?.length > 0 || allStoreProducts.length > 0) &&
          <Card>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <TabContext value={selectedTab}>
                <Tabs
                  value={selectedTab}
                  onChange={handleChangeTab}
                  className={classes.rootTabs}
                >
                  <Tab
                    className={classes.tabsTitles}
                    label={`Active (${activeProducts.length})`}
                    value="active"
                    component={Link}
                    to="/store-products/list/active"
                  />
                  <Tab
                    className={classes.tabsTitles}
                    label={`Draft (${disabledProducts.length})`}
                    value="draft"
                    component={Link}
                    to="/store-products/list/draft"
                  />
                  {!!queueTasks.length &&
                  <Tab
                    className={classes.tabsTitles}
                    label={`Queue (${queueTasks.length})`}
                    value="queue"
                    component={Link}
                    to="/store-products/list/queue"
                  />}
                </Tabs>
                <TabPanel className="p-0" value="active">
                  <StoreProductsList
                    productsList={activeProducts}
                    queueTasks={queueTasks}
                    queueTasksSkus={queueTasksSkus}
                  />
                </TabPanel>
                <TabPanel className="p-0" value="draft">
                  <StoreProductsList
                    productsList={disabledProducts}
                    queueTasks={queueTasks}
                    queueTasksSkus={queueTasksSkus}
                  />
                </TabPanel>
                <TabPanel className="p-0" value="queue">
                  <StoreProductsQueue queueTasks={queueTasks} />
                </TabPanel>
              </TabContext>
            </Box>
          </Card>
          }
        </div>
        <BackdropCircular loading={deletingProduct} />
      </div>
    </>
  );
};

export default StoreProducts;
